import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, FaFileAlt, FaChartLine, FaSpinner, FaCog, FaExclamationTriangle, 
  FaPlus, FaTimes, FaCheck, FaGlobe, FaPhone, FaEnvelope, FaBuilding,
  FaUser, FaCertificate, FaIdCard, FaMapMarkerAlt, FaExternalLinkAlt, FaUserPlus, FaUserTie, FaGift
} from 'react-icons/fa';
import axios from 'axios';
import ManagerDashboard from './ManagerDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import ClientDashboard from './ClientDashboard';
import GrowthPartnerDashboard from './GrowthPartnerDashboard';
import ContentLibrary from '../components/ContentLibrary';
import PendingChildFutureTests from '../components/advisor/PendingChildFutureTests';
import ClientReferrals from '../components/advisor/ClientReferrals';
import ClientNomineesView from '../components/advisor/ClientNomineesView';
import ReferAdvisorModal from '../components/advisor/ReferAdvisorModal';
import MyAdvisorReferrals from '../components/advisor/MyAdvisorReferrals';
import { AdvisorClientsSection, AnnouncementsSection, ServiceInterestsSection, AddClientModal } from '../components/advisor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientLimitInfo, setClientLimitInfo] = useState(null);
  const [branding, setBranding] = useState(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpNotes, setTopUpNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '', description: '', announcement_type: 'general', event_date: '', event_time: '', event_link: '', priority: 'normal'
  });
  const [serviceInterests, setServiceInterests] = useState([]);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showReferAdvisorModal, setShowReferAdvisorModal] = useState(false);
  const [advisorReferralsKey, setAdvisorReferralsKey] = useState(0);

  const getToken = useCallback(() => localStorage.getItem('token'), []);
  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) { /* silenced */ }
    finally { setLoading(false); }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisor/branding`, { headers: headers() });
      setBranding(response.data);
    } catch (error) { /* silenced */ }
  }, [headers]);

  const fetchClientLimitInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisor/client-limit`, { headers: headers() });
      setClientLimitInfo(response.data);
    } catch (error) { /* silenced */ }
  }, [headers]);

  const fetchAllClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisor/all-clients`, { headers: headers() });
      const clients = response.data || [];
      setPendingSubmissions(clients.filter(c => c.profile_status === 'submitted'));
      setAllClients(clients);
    } catch (error) { /* silenced */ }
  }, [headers]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisor/announcements`, { headers: headers() });
      setAnnouncements(response.data || []);
    } catch (error) { /* silenced */ }
  }, [headers]);

  const fetchServiceInterests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisor/service-interests`, { headers: headers() });
      setServiceInterests(response.data || []);
    } catch (error) { /* silenced */ }
  }, [headers]);

  useEffect(() => {
    fetchStats();
    if (user?.role === 'advisor') {
      fetchClientLimitInfo();
      fetchBranding();
      fetchAllClients();
      fetchAnnouncements();
      fetchServiceInterests();
    }
  }, [user, fetchStats, fetchBranding, fetchClientLimitInfo, fetchAllClients, fetchAnnouncements, fetchServiceInterests]);

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.description) {
      setMessage({ type: 'error', text: 'Please fill in title and description' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/announcements`, announcementForm, { headers: headers() });
      setMessage({ type: 'success', text: 'Announcement created successfully!' });
      setShowAnnouncementModal(false);
      setAnnouncementForm({ title: '', description: '', announcement_type: 'general', event_date: '', event_time: '', event_link: '', priority: 'normal' });
      fetchAnnouncements();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create announcement' }); }
    finally { setSubmitting(false); }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/advisor/announcements/${announcementId}`, { headers: headers() });
      setMessage({ type: 'success', text: 'Announcement deleted!' });
      fetchAnnouncements();
    } catch (error) { setMessage({ type: 'error', text: 'Failed to delete announcement' }); }
  };

  const handleMarkContacted = async (interestId) => {
    try {
      await axios.put(`${API}/advisor/service-interests/${interestId}/contacted`, {}, { headers: headers() });
      setMessage({ type: 'success', text: 'Marked as contacted!' });
      fetchServiceInterests();
    } catch (error) { setMessage({ type: 'error', text: 'Failed to update status' }); }
  };

  const handleGrantReportAccess = async (clientId, clientName) => {
    if (!window.confirm(`Grant ${clientName} access to view their report?`)) return;
    setActionLoading(prev => ({ ...prev, [clientId]: 'report' }));
    try {
      await axios.post(`${API}/advisor/grant-report-access/${clientId}`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `Report access granted to ${clientName}!` });
      fetchAllClients();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to grant access' }); }
    finally { setActionLoading(prev => ({ ...prev, [clientId]: null })); }
  };

  const handleGrantDownloadAccess = async (clientId, clientName) => {
    if (!window.confirm(`Enable ${clientName} to download their PDF report?`)) return;
    setActionLoading(prev => ({ ...prev, [clientId]: 'download' }));
    try {
      await axios.post(`${API}/advisor/grant-download-access/${clientId}`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `Download access granted to ${clientName}!` });
      fetchAllClients();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to grant download access' }); }
    finally { setActionLoading(prev => ({ ...prev, [clientId]: null })); }
  };

  const handleRequestTopUp = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/request-topup`, { notes: topUpNotes }, { headers: headers() });
      setMessage({ type: 'success', text: 'Top-up request submitted successfully! Please contact your manager or MDRT School Customer Care Team.' });
      setShowTopUpModal(false);
      setTopUpNotes('');
      fetchClientLimitInfo();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to submit request' }); }
    finally { setSubmitting(false); }
  };

  // Route to role-specific dashboards
  if (user?.role === 'super_admin') return <SuperAdminDashboard />;
  if (user?.role === 'manager') return <ManagerDashboard />;
  if (user?.role === 'client') return <ClientDashboard />;
  if (user?.role === 'growth_partner') return <GrowthPartnerDashboard />;
  if (loading) return <div className="flex items-center justify-center h-96"><FaSpinner className="animate-spin text-[#FFD700]" size={48} /></div>;

  return (
    <div className="space-y-6">
      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border ${message.type === 'success' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-red-500/20 border-red-500 text-red-400'}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right text-white hover:opacity-70"><FaTimes /></button>
        </div>
      )}

      {/* Welcome Header with Profile Card */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl">
        {/* Refer an Advisor Banner - Top Most Visible (Advisor Only) */}
        {user?.role === 'advisor' && (
          <div 
            onClick={() => setShowReferAdvisorModal(true)}
            className="mb-6 p-4 bg-gradient-to-r from-purple-500/20 via-violet-500/20 to-purple-500/20 rounded-xl border-2 border-purple-500/50 cursor-pointer hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/20 transition-all group"
            data-testid="refer-advisor-banner"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <FaUserTie size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg flex items-center">
                    <FaGift className="text-[#FFD700] mr-2" />
                    Refer an Advisor & Earn ₹1,500/year!
                  </h3>
                  <p className="text-gray-300 text-sm">Know a financial advisor? Refer them to Arth Gurukul and earn rewards!</p>
                </div>
              </div>
              <button 
                className="px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0A1929] font-bold rounded-xl hover:shadow-lg hover:shadow-[#FFD700]/30 transition-all flex items-center space-x-2 whitespace-nowrap"
              >
                <FaUserTie />
                <span>Refer Now</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Welcome Message */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Welcome back, <span className="text-[#FFD700]">{user?.name}!</span></h1>
            <p className="text-gray-300 mb-4">Manage your clients and create comprehensive financial plans</p>
          </div>
          
          {/* Advisor Profile Card */}
          {branding && (
            <div className="bg-[#0F172A]/50 rounded-xl p-5 border border-[#2D5A8F] min-w-[300px]" data-testid="advisor-profile-card">
              {/* Profile Photo and Name */}
              <div className="flex items-center space-x-4 mb-4">
                {branding.profile_photo ? (
                  <img 
                    src={branding.profile_photo} 
                    alt={branding.name || user?.name} 
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#FFD700]"
                    data-testid="advisor-profile-photo"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#1E3A5F] flex items-center justify-center border-2 border-[#2D5A8F]">
                    <FaUser className="text-gray-400 text-2xl" />
                  </div>
                )}
                <div>
                  <p className="text-white font-bold text-lg">{branding.name || user?.name}</p>
                  {branding.designation && (
                    <p className="text-[#FFD700] text-sm">{branding.designation}</p>
                  )}
                  {branding.city && (
                    <p className="text-gray-400 text-xs flex items-center mt-1">
                      <FaMapMarkerAlt className="mr-1" /> {branding.city}
                    </p>
                  )}
                </div>
              </div>

              {/* Company Info */}
              {branding.company && (
                <div className="flex items-center space-x-3 mb-3 pb-3 border-b border-[#2D5A8F]">
                  {branding.company_logo ? (
                    <img 
                      src={branding.company_logo} 
                      alt={branding.company} 
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <FaBuilding className="text-[#FFD700] text-lg" />
                  )}
                  <p className="text-gray-300 text-sm">{branding.company}</p>
                </div>
              )}

              {/* Certifications */}
              {(branding.certifications || branding.irda_licence || branding.amfi_licence || branding.ira_number || branding.other_certification) && (
                <div className="mb-3 pb-3 border-b border-[#2D5A8F]">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-2 flex items-center">
                    <FaCertificate className="mr-1 text-[#FFD700]" /> Certifications
                  </p>
                  <div className="space-y-1">
                    {branding.certifications && (
                      <p className="text-gray-300 text-sm">{branding.certifications}</p>
                    )}
                    {branding.irda_licence && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">IRDA:</span>&nbsp;{branding.irda_licence}
                      </p>
                    )}
                    {branding.amfi_licence && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">AMFI:</span>&nbsp;{branding.amfi_licence}
                      </p>
                    )}
                    {branding.ira_number && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">IRA:</span>&nbsp;{branding.ira_number}
                      </p>
                    )}
                    {branding.other_certification && (
                      <p className="text-gray-400 text-xs">{branding.other_certification}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              <div className="space-y-2">
                {branding.contact && (
                  <a 
                    href={`tel:${branding.contact}`}
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors"
                  >
                    <FaPhone className="mr-2 text-[#FFD700]" />
                    {branding.contact}
                  </a>
                )}
                {branding.email && (
                  <a 
                    href={`mailto:${branding.email}`}
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors"
                  >
                    <FaEnvelope className="mr-2 text-[#FFD700]" />
                    {branding.email}
                  </a>
                )}
                {branding.website && (
                  <a 
                    href={branding.website.startsWith('http') ? branding.website : `https://${branding.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors group"
                  >
                    <FaGlobe className="mr-2 text-[#FFD700]" />
                    <span className="group-hover:underline">{branding.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                    <FaExternalLinkAlt className="ml-2 text-xs text-gray-500" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Client Limit Card */}
      {user?.role === 'advisor' && clientLimitInfo && (
        <div className={`rounded-xl p-6 border shadow-lg ${clientLimitInfo.is_at_limit ? 'bg-gradient-to-br from-red-900/30 to-red-800/30 border-red-500' : 'bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] border-[#2D5A8F]'}`} data-testid="client-limit-card">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <FaUsers className={clientLimitInfo.is_at_limit ? 'text-red-400' : 'text-[#FFD700]'} size={24} />
                <h3 className="text-lg font-bold text-white">Client Usage</h3>
                {clientLimitInfo.is_at_limit && <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full flex items-center"><FaExclamationTriangle className="mr-1" /> PAID LIMIT REACHED</span>}
                {clientLimitInfo.has_pending_request && <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">TOP-UP PENDING</span>}
              </div>
              {/* Free/Paid breakdown */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                  <p className="text-cyan-400 text-xl font-bold">{clientLimitInfo.free_clients_count || 0}</p>
                  <p className="text-gray-400 text-xs">Free Clients <span className="text-cyan-400/70">(Unlimited)</span></p>
                </div>
                <div className={`${clientLimitInfo.is_at_limit ? 'bg-red-500/10 border-red-500/20' : 'bg-[#FFD700]/10 border-[#FFD700]/20'} border rounded-lg p-3`}>
                  <p className={`text-xl font-bold ${clientLimitInfo.is_at_limit ? 'text-red-400' : 'text-[#FFD700]'}`}>{clientLimitInfo.paid_clients_count || 0}<span className="text-gray-400 text-sm font-normal"> / {clientLimitInfo.client_limit}</span></p>
                  <p className="text-gray-400 text-xs">Paid Clients <span className={clientLimitInfo.is_at_limit ? 'text-red-400' : 'text-green-400'}>({clientLimitInfo.clients_remaining} left)</span></p>
                </div>
              </div>
              {/* Progress bar for paid only */}
              <div className="w-full bg-[#0F172A] rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${clientLimitInfo.is_at_limit ? 'bg-gradient-to-r from-red-500 to-red-400' : (clientLimitInfo.paid_clients_count || 0) / clientLimitInfo.client_limit > 0.8 ? 'bg-gradient-to-r from-yellow-500 to-yellow-400' : 'bg-gradient-to-r from-green-500 to-green-400'}`} style={{ width: `${Math.min(100, ((clientLimitInfo.paid_clients_count || 0) / clientLimitInfo.client_limit) * 100)}%` }} />
              </div>
              {clientLimitInfo.is_at_limit && <p className="text-red-300 text-sm mt-2">Paid client limit reached. Request a top-up for more paid slots. You can still add free clients.</p>}
            </div>
            <div className="ml-6 flex flex-col space-y-2">
              <button 
                onClick={() => setShowAddClientModal(true)} 
                className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2" 
                data-testid="add-client-btn"
              >
                <FaUserPlus /><span>Add Client</span>
              </button>
              {clientLimitInfo.is_at_limit && !clientLimitInfo.has_pending_request && (
                <button onClick={() => setShowTopUpModal(true)} className="px-4 py-2 bg-[#1E3A5F] border border-[#FFD700]/50 text-[#FFD700] font-medium rounded-lg hover:bg-[#2D5A8F] transition-all flex items-center space-x-2 text-sm" data-testid="request-topup-btn">
                  <FaPlus /><span>Request Paid Top-Up</span>
                </button>
              )}
              {clientLimitInfo.has_pending_request && <div className="text-center"><p className="text-yellow-400 text-sm font-medium">Request Pending</p><p className="text-gray-400 text-xs">Awaiting approval</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg hover:shadow-2xl transition-shadow">
          <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm font-medium">Total Clients</p><p className="text-4xl font-bold text-white mt-2">{stats?.total_clients || 0}</p></div><div className="p-4 bg-[#FFD700] bg-opacity-20 rounded-full"><FaUsers className="text-[#FFD700]" size={32} /></div></div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]"><p className="text-gray-400 text-xs">Active client accounts</p></div>
        </div>
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg hover:shadow-2xl transition-shadow">
          <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm font-medium">Financial Plans</p><p className="text-4xl font-bold text-white mt-2">{stats?.total_plans || 0}</p></div><div className="p-4 bg-[#FFD700] bg-opacity-20 rounded-full"><FaFileAlt className="text-[#FFD700]" size={32} /></div></div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]"><p className="text-gray-400 text-xs">Completed plans</p></div>
        </div>
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg hover:shadow-2xl transition-shadow">
          <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm font-medium">This Month</p><p className="text-4xl font-bold text-white mt-2">New</p></div><div className="p-4 bg-[#FFD700] bg-opacity-20 rounded-full"><FaChartLine className="text-[#FFD700]" size={32} /></div></div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]"><p className="text-gray-400 text-xs">Recent activity</p></div>
        </div>
      </div>

      {/* Advisor-specific sections */}
      {user?.role === 'advisor' && (
        <>
          <AdvisorClientsSection
            allClients={allClients}
            pendingSubmissions={pendingSubmissions}
            actionLoading={actionLoading}
            handleGrantReportAccess={handleGrantReportAccess}
            handleGrantDownloadAccess={handleGrantDownloadAccess}
          />

          {/* Quick Actions */}
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl">
            <h2 className="text-2xl font-bold text-white mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button onClick={() => { if (clientLimitInfo?.is_at_limit) { setMessage({ type: 'error', text: 'Client limit reached. Please request a top-up to add more clients.' }); return; } navigate('/dashboard/clients/new'); }}
                className={`p-4 font-bold rounded-lg transition-all transform hover:scale-105 ${clientLimitInfo?.is_at_limit ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] hover:from-[#FFD700] hover:to-[#D4AF37]'}`}
                data-testid="add-new-client-btn">
                + Add New Client{clientLimitInfo?.is_at_limit && <span className="block text-xs mt-1">Limit Reached</span>}
              </button>
              <button className="p-4 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]">View Reports</button>
              <button className="p-4 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]">✨ AI Insights</button>
              <button onClick={() => navigate('/dashboard/settings')} className="p-4 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center justify-center space-x-2"><FaCog /><span>Settings</span></button>
            </div>
          </div>

          {/* AI Feature Banner */}
          <div className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded-xl p-8 shadow-xl">
            <div className="flex items-center space-x-4">
              <div className="text-5xl">✨</div>
              <div><h3 className="text-2xl font-bold text-[#0A1929] mb-2">AI-Powered Financial Planning</h3><p className="text-[#0A1929] opacity-80">Get intelligent recommendations, risk assessments, and goal prioritization powered by Google Gemini AI.</p></div>
            </div>
          </div>

          <AnnouncementsSection
            announcements={announcements}
            showModal={showAnnouncementModal}
            setShowModal={setShowAnnouncementModal}
            announcementForm={announcementForm}
            setAnnouncementForm={setAnnouncementForm}
            handleCreate={handleCreateAnnouncement}
            handleDelete={handleDeleteAnnouncement}
            submitting={submitting}
          />

          <ServiceInterestsSection serviceInterests={serviceInterests} handleMarkContacted={handleMarkContacted} />

          {/* Client Referrals */}
          <ClientReferrals />

          {/* My Advisor Referrals */}
          <MyAdvisorReferrals key={advisorReferralsKey} />

          {/* Client Nominees (Family Vault) */}
          <ClientNomineesView />

          {/* Pending Child Future Tests */}
          <PendingChildFutureTests />
        </>
      )}

      <ContentLibrary title="Paiso Ki Pathshala - Learning Resources" />

      {/* Refer Advisor Modal */}
      <ReferAdvisorModal 
        isOpen={showReferAdvisorModal}
        onClose={() => setShowReferAdvisorModal(false)}
        onSuccess={() => setAdvisorReferralsKey(prev => prev + 1)}
      />

      {/* Top-Up Request Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#1A2942] rounded-xl p-6 border border-[#2D5A8F] max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Request Client Limit Top-Up</h3>
            <div className="bg-[#0F172A] rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2"><span className="text-gray-400">Current Limit:</span><span className="text-white font-bold">{clientLimitInfo?.client_limit} clients</span></div>
              <div className="flex justify-between items-center"><span className="text-gray-400">Requesting Additional:</span><span className="text-[#FFD700] font-bold">+50 clients</span></div>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-3 mb-4">
              <p className="text-yellow-400 text-sm"><FaExclamationTriangle className="inline mr-2" />As per current policy, connect with your manager or the Customer Care Team of MDRT School for faster processing.</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-medium mb-2">Notes (Optional)</label>
              <textarea value={topUpNotes} onChange={(e) => setTopUpNotes(e.target.value)} placeholder="Add any additional notes for your request..." rows={3} className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none resize-none" />
            </div>
            <div className="flex space-x-4">
              <button onClick={() => { setShowTopUpModal(false); setTopUpNotes(''); }} className="flex-1 py-3 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]">Cancel</button>
              <button onClick={handleRequestTopUp} disabled={submitting} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2" data-testid="submit-topup-btn">
                {submitting ? <FaSpinner className="animate-spin" /> : <FaCheck />}<span>{submitting ? 'Submitting...' : 'Submit Request'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      <AddClientModal 
        isOpen={showAddClientModal} 
        onClose={() => setShowAddClientModal(false)} 
        onSuccess={() => {
          fetchAllClients();
          fetchClientLimitInfo();
        }}
        clientLimitInfo={clientLimitInfo}
      />
    </div>
  );
};

export default Dashboard;
